/* BrandBadge
 * ─────────────────────────────────────────────────────────────────
 * Pill-style badge for athletes tagged into ANY brand network (Hope,
 * Flythouse-managed brand partners, etc). Generic counterpart to the
 * HOPE-specific badge — pulls its label, ring colors, and link target
 * from the corresponding `brand_networks` document.
 *
 * Visual:
 *   - Black fill, white display text (matches Emmy / HOPE silhouette)
 *   - Inner ring: brand.accent_color
 *   - Outer ring: brand.secondary_color
 *   - Optional mini brand.logo thumbnail prefix
 *
 * Routes to /brand/{brand.slug}. Click swallows propagation so it doesn't
 * trigger a parent card link.
 *
 * Variants:
 *   pill      — absolute -bottom-1 -right-1 on its nearest relative parent
 *   inline    — render inline next to a name / heading
 *   absolute  — legacy alias for pill
 */
import { Link } from 'react-router-dom';
import { resolveImageUrl } from './ImageUpload';

const SIZE_MAP = {
  sm: { pad: 'px-1.5 py-[1px]', text: 'text-[9px]',  logo: 'w-2.5 h-2.5' },
  md: { pad: 'px-2 py-0.5',     text: 'text-[10px]', logo: 'w-3 h-3' },
  lg: { pad: 'px-2.5 py-1',     text: 'text-[12px]', logo: 'w-3.5 h-3.5' },
};

const BrandBadge = ({ brand, variant = 'pill', size = 'md', className = '' }) => {
  if (!brand || !brand.slug) return null;
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const accent    = brand.accent_color    || '#22D3EE';
  const secondary = brand.secondary_color || accent;
  const ring = {
    boxShadow:
      `inset 0 0 0 1.5px ${accent}, 0 0 0 1.5px ${secondary}, 0 6px 16px -6px ${secondary}55`,
  };

  // Short label rule: brand.badge_label (if author set one) > brand.name
  // shortened to first word in uppercase. Keeps the chip compact next to
  // an athlete avatar where horizontal space is tight.
  const labelRaw = (brand.badge_label || brand.name || brand.slug).toString();
  const label = labelRaw.toUpperCase();

  const core = `inline-flex items-center gap-1 ${s.pad} ${s.text} font-display tracking-[0.18em] uppercase text-white bg-black rounded-full select-none transition-transform hover:scale-105 active:scale-95`;
  const href = `/brand/${brand.slug}`;
  const stop = (e) => e.stopPropagation();

  const inner = (
    <>
      {brand.logo && (
        <span
          className={`${s.logo} rounded-full bg-white/10 overflow-hidden inline-flex items-center justify-center flex-shrink-0`}
        >
          <img
            src={resolveImageUrl(brand.logo, 80)}
            alt=""
            className="w-full h-full object-contain"
          />
        </span>
      )}
      {label}
    </>
  );

  if (variant === 'inline') {
    return (
      <Link
        to={href}
        onClick={stop}
        data-testid={`brand-badge-${brand.slug}`}
        className={`${core} ${className}`}
        style={ring}
        title={`Open ${brand.name} brand page`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <Link
      to={href}
      onClick={stop}
      data-testid={`brand-badge-${brand.slug}`}
      className={`absolute -bottom-1 -right-1 z-10 ${core} shadow-md ${className}`}
      style={ring}
      title={`Open ${brand.name} brand page`}
    >
      {inner}
    </Link>
  );
};

export default BrandBadge;
